<template>
  <div class="base-collapsible-table-view">
    <div class="common-table__row " :class="{'common-table__row_highlighted': isHighlighted,
    'common-table__row_comparison': isComparisonModeHighlighted}"
         :style="paddingStyle">
      <div class="title-cell row-header base-collapsible-table-cell" v-if="data.header">{{data.header}}</div>
      <template v-else>
        <div class="common-table__cell title-cell base-collapsible-table-cell"
             :class="`base-collapsible-table-cell__${currentConfig.type}`"
             :style="firstConfigCell.style">
          <div class="base-collapsible-table-cell__title" :style="calculatePadding">
            <template v-if="!isThirdLevel">
              <span class="base-collapsible-table-cell__color" :style="itemColorClass" v-if="itemColorClass"></span>
              <span v-if="firstConfigCell.searchInGoogle">
                <base-search-in-google :query="data[firstConfigCell.propName]" />
              </span>
              <span class="title-cell__name"
                    v-bind:private-content="data['type'] === 'Clients'"
                    :class="{'title-cell__name-selectable': currentConfig.clickable, 'title-cell__with-search': firstConfigCell.searchInGoogle}"
                    @click.stop="currentConfig.clickable
                      && $emit('cellClick', {
                        target: filterData(data[firstConfigCell.propName], firstConfigCell.filterName, firstConfigCell.filterParam),
                        configLevel: currentConfig
                      }
                    )">
                <base-truncate-label v-if="truncateUrl"
                                     :label="filterData(data[firstConfigCell.propName], firstConfigCell.filterName, firstConfigCell.filterParam)"
                                     font="13px Roboto"
                                     :shrink="0.60"
                                     watchClass="title-cell__name" />
                <template v-else>
                  {{ filterData(data[firstConfigCell.propName], firstConfigCell.filterName, firstConfigCell.filterParam) }}
                </template>
              </span>
            </template>
            <template v-else>
              <span v-if="showLinkToCB">
              <a :href="data[currentConfig.linkConfig.contentBriefUrl]"
                 target="_blank">
                <base-material-design-icon icon="file"/>
              </a>
              </span>
<!--              <span v-if="showCalendar">-->
<!--              <base-material-design-icon-->
<!--                icon="calendar"-->
<!--                @click="currentConfig.handleCalendarIconClick(data)" />-->
<!--              </span>-->
              <a :href="data[currentConfig.linkConfig.hrefProp]"
                 class="title-cell__name title-cell__third-level-title"
                 :title="data[currentConfig.linkConfig.linkProp]"
                 target="_blank">
                <base-truncate-label :label="data[currentConfig.linkConfig.linkProp]"
                                     :watchClass="'title-cell__third-level-title'"
                />
              </a>
            </template>
          </div>
          <div style="position: relative" v-if="isCollapsible">
            <base-material-design-icon
              class="base-collapsible-table-cell__arrow"
              :icon="icon"
              :iconStyle="{color: '#284aff', 'font-size': '17px'}"
              @click="onArrowIconClick()" />
          </div>
        </div>
        <div class="common-table__cell base-collapsible-table-cell"
             v-for="(cell) in otherConfigCells"
             :key="cell.propName"
             :class="[`base-collapsible-table-cell__${currentConfig.type}`]"
             :style="cell.style">
          <template>
            <span v-tooltip.top-middle="{content: getTooltipContent(cell.propName), ...rowTooltipOptions}"
                  :class="{'title-cell__name-selectable': cell.clickable, 'base-collapsible-table-cell__bold': isExpanded}"
                  @click.stop="cell.clickable && $emit('cellClick', { target: data, cellConfig: cell })">
              {{filterIntegerData(data[cell.propName], cell.filterName, cell.filterParam)}}
            </span>
          </template>
        </div>
      </template>
    </div>
    <template v-if="isExpanded && children && children.length">
      <div v-for="(row) in preparedChildren" :key="getRowId(row)">
        <BaseTableCollapsibleRow
          :data="row"
          @cellClick="$emit('cellClick', $event)"
          :config="config"
          :level="level + 1"
          :show-calendar="showCalendar"
          :showLinkToCB="showLinkToCB"
          :is-keywords-modal="isKeywordsModal"
          ref="table_row"
        />
      </div>
      <div class="pagination-wrapper" v-if="hasPagination">
        <base-pagination :totalItems="children.length"
                         :limit="currentConfig.childrenLimit"
                         @pageChanged="prepareChildren"/>
      </div>
    </template>
    <template v-else-if="isLoaderVizible">
      <div class="common-table__row base-collapsible-table-cell__loader-wrapper">
        <base-horizontal-loader class="base-collapsible-table-cell__loader"/>
      </div>
    </template>
    <template v-else-if="isNoDataVisible">
        <div class="base-collapsible-table-cell__not-found">No records found</div>
    </template>
  </div>
</template>

<script>
import filters from '@/common/Filters'
import { mapState, mapActions } from 'vuex'

const icons = {
  angleRight: 'arrowRight',
  angleDown: 'arrowDown'
}
const allColors = [
  '#284aff', '#77d781'
]
/**
 * general row component for {@link BaseTableCollapsible} tables with expanded child rows
 */
export default {
  name: 'BaseTableCollapsibleRow',
  /* see description of below props in BaseTableCollapsible component */
  props: {
    config: {
      type: Array,
      required: true
    },
    data: {
      type: Object,
      required: true
    },
    level: {
      type: Number,
      default: 0
    },
    paddingLeft: {
      type: Number,
      default: 0
    },
    showCalendar: {
      type: Boolean,
      default: true
    },
    truncateUrl: {
      type: Boolean,
      default: false
    },
    showLinkToCB: {
      type: Boolean,
      default: true
    },
    isKeywordsModal: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      isExpanded: false,
      icon: icons.angleRight,
      preparedChildren: []
    }
  },
  watch: {
    children: {
      handler () {
        if (this.isExpanded) {
          const topEdge = this.currentConfig.childrenLimit
            ? this.currentConfig.childrenLimit
            : this.children.length
          this.prepareChildren({ from: 0, to: topEdge })
        }
      },
      deep: true
    }
  },
  computed: {
    ...mapState('rankZoneCommon', ['expandedItem']),
    currentConfig () {
      return this.config[this.level]
    },
    childrenProp () {
      return this.currentConfig.children
    },
    isCollapsible () {
      // No config found
      if (!this.currentConfig) {
        return false
      }
      const currentType = this.currentConfig.type

      // Depends on level name
      if (currentType === 'fourth-level' || currentType === 'total-level' || currentType === 'ordinary-level') {
        return false
      }
      // Depends on config
      return this.childrenProp
    },
    isThirdLevel () {
      return this.currentConfig.type === 'third-level'
    },
    isFourthLevel () {
      return this.currentConfig.type === 'fourth-level'
    },
    isFirstLevel () {
      return this.currentConfig.type === 'first-level'
    },
    firstConfigCell () {
      return this.currentConfig.cells[0]
    },
    lastConfigCell () {
      const cells = this.currentConfig.cells
      return cells[cells.length - 1]
    },
    otherConfigCells () {
      return this.currentConfig.cells.slice(1)
    },
    firstDataCel () {
      return this.data[0]
    },
    lastDataCell () {
      return this.data[this.data.length - 1]
    },
    otherDataCells () {
      return this.data.slice(1)
    },
    children () {
      return this.data[this.childrenProp]
    },
    isHighlighted () {
      const result = Boolean(this.currentConfig.highlightValue &&
          (this.data[this.currentConfig.highlightProp] === this.currentConfig.highlightValue))
      return result
    },
    isComparisonModeHighlighted () {
      if (!this.currentConfig.colorMap || !this.currentConfig.colorMap.length) {
        return false
      }
      const { colorMap, colorProperty } = this.currentConfig
      if (!colorMap || !colorProperty) {
        return false
      }
      const index = colorMap.findIndex(item => item === this.data[colorProperty])
      return index === 1
    },
    /* notify if this row's child should be loaded from backend */
    isChildrenLoadingLevel () {
      return this.currentConfig.isChildrenLoadingLevel
    },
    isLoaderVizible () {
      return this.isChildrenLoadingLevel && this.isExpanded && (!this.children || !this.children.length) && this.expandedItem
    },
    isNoDataVisible (){
      return this.isExpanded && (!this.children || !this.children.length)
    },
    hasPagination () {
      const limit = this.currentConfig.childrenLimit
      return limit && limit < this.children.length
    },
    calculatePadding () {
      let padding = 0
      switch (this.level) {
        case 1:
          padding = 10
          break
        case 2:
          padding = 25
          break
        case 3:
          padding = 70
          break
      }
      if (this.level === 1 && this.isFourthLevel) {
        padding = 47
      }
      return { 'padding-left': `${padding}px` }
    },
    paddingStyle () {
      return {
        paddingLeft: `${this.paddingLeft}px`
      }
    },
    isHasTooltip () {
      return this.data.tooltipType
    },
    rowTooltipOptions () {
      const options = {
        classes: `rfx-tooltip-arrow ${!this.isHasTooltip && 'base-collapsible-table-cell__hide_tooltip'}`,
        trigger: 'hover'
      }
      return Object.assign({}, this.tooltipOptions, options)
    },
    /* control indicator color */
    itemColorClass () {
      const { colorMap, colorProperty } = this.currentConfig
      if (!colorMap || !colorProperty) {
        return
      }
      const index = colorMap.findIndex(item => item === this.data[colorProperty])
      if (index >= 0) {
        return { 'background-color': allColors[index] }
      }
    }
  },
  methods: {
    ...mapActions('rankZoneCommon', ['addExpandedItem']),
    /* notify parent component via store about expanded item to start loading of child data */
    handleChildLoad: function () {
      if (this.isExpanded && this.isChildrenLoadingLevel && !this.preparedChildren.length) {
        this.addExpandedItem(this.data)
      }
    },
    onArrowIconClick () {
      this.isExpanded = !this.isExpanded
      this.icon = this.icon === icons.angleRight ? icons.angleDown : icons.angleRight
      if (!this.isExpanded) {
        return
      }
      const topEdge = this.currentConfig.childrenLimit
        ? this.currentConfig.childrenLimit
        : this.children.length
      this.prepareChildren({ from: 0, to: topEdge })
      this.handleChildLoad()
    },
    filterData (value, filterName, filterParam) {
      const filters = this.$options.filters
      const targetFilter = filters[filterName]
      if (!filterName || !targetFilter) {
        return value
      }
      if (!filterParam) {
        return targetFilter(value)
      }
      return targetFilter(value, filterParam)
    },
    /* filter integer data based on filterName property from cell config */
    filterIntegerData (value, filterName, filterParam) {
      return this.filterData(parseInt(value), filterName, filterParam)
    },
    /* invoked for child pagination */
    prepareChildren (props) {
      this.preparedChildren = this.children.slice(props.from, props.to)
    },
    /* collapse child table outside component */
    collapseTable () {
      if (!this.isExpanded) {
        return
      }
      this.onArrowIconClick()
      this.$refs['table_row'].forEach(item => item.collapseTable())
    },
    getRowId (row) {
      return row._id ? row._id : row.label
    },
    getTooltipContent (cellName) {
      return this.isHasTooltip && `${this.data.tooltipType === 'keyword' ? 'Keyword Count' : 'MSV'}: ${this.$options.filters.commaDividedNumber(this.data[this.data.tooltipType][cellName])}`
    }
  },
  filters: {
    ...filters
  }
}
</script>

<style lang="scss">
 .base-collapsible-table-cell {
   &__hide_tooltip {
     display: none !important;
   }
 }
</style>
<style scoped lang="scss">
  .base-collapsible-table-cell {
    border-bottom: 1px solid rgba(0, 0, 0, .05);
    box-sizing: border-box;
    padding-left: 0;
    color: $rfx-light-march;
    @include ellipsis;
    padding-right: 0;

    &__arrow {
      position: absolute;
      right: 55px;
      cursor: pointer;
    }

    &__bold {
      font-weight: 700;
    }

    &__title {
      display: flex;
      overflow: hidden;
      margin-right: 15px;
      width: 80%;

      .material-design-icon {
        display: flex;
        align-items: center;
        font-size: 16px;
        margin-right: 12px;
        cursor: pointer;
        color: $rfx-dark-march;

        &:hover {
          color: $rfx-bright-blue;
        }
      }
    }

    &__color {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      display: inline-block;
      margin: 4px 10px 1px 0;
      flex-shrink: 0;
    }

    &__header{
      color: $rfx-dark-march;
      font-weight: bold;
    }
    &__ordinary-level:first-child,
    &__top-level:first-child,
    &__second-level:first-child {
      color: $rfx-dark-march;
      font-weight: bold;
    }

    &__ordinary-level:last-child {
      color: $rfx-light-march;
      font-weight: bold;
    }

    &__second-level {
      background-color: $rfx-almost-white;
    }

    &__second-level:last-child {
      font-weight: bold;
      color: $rfx-light-march;
    }

    &__third-level {

      &:first-child {
        color: $rfx-blue;
      }
    }

    &__fourth-level:first-child {
      color: $rfx-light-march;
      font-weight: normal;
    }

    &__third-level:last-child,
    &__fourth-level:last-child {
      background-color: $rfx-almost-white-lighter;
    }

    &__fourth-level {
      padding-top: 8px;
      padding-bottom: 8px;

      &:first-child {
        color: $rfx-light-march;
      }
    }

    &:last-child {
      padding-left: 12px;
      font-weight: bold;
    }

    &__total-level {
      color: $rfx-black-lighter;
      font-weight: bold;
    }

    &_top-level-reveald {
      font-weight: bold;
    }

    &__loader {
      position: absolute;
      top: -2px;
      left: 50%;
      transform: translateX(-50%);
    }
    &__loader-wrapper{
      position: relative;
      height: 60px;
      border-bottom: 1px solid rgba(0, 0, 0, .05);
    }
    &__not-found {
      font-size: 16px;
      font-weight: 600;
      display: flex;
      align-items: center;
      justify-content: center;
      height: 60px;
      color: $rfx-medium-grey;
      border-bottom: 1px solid rgba(0, 0, 0, .05);
    }
  }

  .row-header {
    padding: 20px;
    background-color: #f5f5f5;
    text-align: center;
    width: 100%;
    display: inline-block !important;
  }

  .title-cell {
    display: flex;
    padding-left: 20px;
    justify-content: space-between;

    &__icon {
      margin-left: 5px;
    }

    &__with-search {
      padding-left: 29px;
    }

    &__name {
      color: inherit;
      @include ellipsis;

      &-selectable {
        &:hover {
          cursor: pointer;
          color: $rfx-bright-blue !important;
          text-decoration: underline;
        }
      }
    }

    &__third-level-title {
      width: 100%;
    }

  }

  .common-table__row_comparison {
    background-color: $rfx-almost-white-lighter;
    }

  .pagination-wrapper {
    display: flex;
    height: 60px;
    justify-content: center;
    align-items: center;
    border-bottom: 1px solid $rfx-almost-white;
  }
</style>
